import React, { useEffect, useRef } from'react';
import { gsap } from'../../animations/gsap.config';
import { ScrollTrigger } from'../../animations/gsap.config';
import contactData from'../../data/contact.json';
import { ArrowDown } from'lucide-react';



/* ─────────────────────────────────────────────
 SCROLL INDICATOR
───────────────────────────────────────────── */
const ScrollIndicator = () => {
 const lineRef = useRef(null);

 useEffect(() => {
 gsap.to(lineRef.current, {
 scaleY: 0,
 transformOrigin:'top center',
 duration: 0.8,
 ease:'power2.inOut',
 repeat: -1,
 yoyo: true,
 delay: 0.5,
 });
 }, []);

 return (
 <div className="flex flex-col items-center gap-2">
 <span className="text-[10px] font-medium tracking-[0.3em] uppercase text-slate-500">
 Scroll To Connect
 </span>
 <div
 ref={lineRef}
 className="w-px h-10 bg-gradient-to-b from-primary-400 to-transparent"
 />
 <ArrowDown size={12} className="text-slate-500 animate-bounce" />
 </div>
 );
};

/* ─────────────────────────────────────────────
 CONTACT HERO
───────────────────────────────────────────── */
const ContactHero = () => {
 const { hero } = contactData;

 const sectionRef = useRef(null);
 const imgRef = useRef(null);
 const overlayRef = useRef(null);
 const meshRef = useRef(null);
 const headLine1Ref = useRef(null);
 const headLine2Ref = useRef(null);
 const descRef = useRef(null);
 const scrollCueRef = useRef(null);
 const vertLineRef = useRef(null);

 useEffect(() => {
 const ctx = gsap.context(() => {
 const tl = gsap.timeline({ delay: 0.3 });

 // Set initial image state
 gsap.set(imgRef.current, { opacity: 0 });

 tl.fromTo(imgRef.current, { scale: 1.2, filter:'blur(8px)', opacity: 0 }, { scale: 1.03, filter:'blur(0px)', opacity: 1, duration: 2.5, ease:'power3.out' });
 tl.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 1.5, ease:'power2.out' }, 0.3);
 tl.fromTo(headLine1Ref.current, { y: 80, opacity: 0, skewY: 4 }, { y: 0, opacity: 1, skewY: 0, duration: 0.9, ease:'power4.out' }, 0.8);
 tl.fromTo(headLine2Ref.current, { y: 80, opacity: 0, skewY: 4 }, { y: 0, opacity: 1, skewY: 0, duration: 0.9, ease:'power4.out' }, 0.95);
 tl.fromTo(descRef.current, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7, ease:'power3.out' }, 1.2);
 tl.fromTo(scrollCueRef.current, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease:'power3.out' }, 1.5);
 tl.fromTo(vertLineRef.current, { scaleY: 0 }, { scaleY: 1, duration: 0.8, ease:'power3.out', transformOrigin:'top' }, 1.1);

 gsap.to(meshRef.current, { y: -30, x: 20, duration: 8, ease:'sine.inOut', repeat: -1, yoyo: true });
 }, sectionRef);

 return () => ctx.revert();
 }, []);

 useEffect(() => {
 const ctx = gsap.context(() => {
 ScrollTrigger.create({
 trigger: sectionRef.current,
 start:'top top',
 end:'bottom top',
 scrub: true,
 onUpdate: (self) => {
 const p = self.progress;
 gsap.set(imgRef.current, { y: p * 100, scale: 1.03 + p * 0.05 });
 gsap.set(meshRef.current, { y: p * 80 });
 },
 });
 }, sectionRef);

 return () => ctx.revert();
 }, []);

 return (
 <section ref={sectionRef} id="contact-hero" className="relative w-full overflow-hidden bg-white" style={{ height:'70vh', minHeight:'500px', maxHeight:'700px' }}>
 {/* Light mode background */}
 <div className="absolute inset-0 z-0 opacity-100 transition-opacity duration-500" style={{ background:'linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #eff6ff 100%)' }} />
 
 {/* Dark mode background */}
 <div className="absolute inset-0 z-0 opacity-0 transition-opacity duration-500" style={{ background:'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)' }} />
 
 <div className="absolute inset-0 z-[1] overflow-hidden">
 {/* Image loading placeholder */}
 <div className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 animate-pulse" />
 
 <img 
   ref={imgRef} 
   src={hero.image} 
   alt="Contact Agora Consulting" 
   className="w-full h-full object-cover will-change-transform" 
   style={{ 
     transform:'scale(1.2)', 
     objectPosition:'center center',
     filter: 'brightness(0.8) contrast(1.1)',
     opacity: 0
   }} 
   onLoad={() => {
     // Ensure image is visible when loaded and hide placeholder
     if (imgRef.current) {
       gsap.set(imgRef.current.previousElementSibling, { opacity: 0 });
     }
   }}
   onError={(e) => {
     console.error('Contact hero image failed to load:', hero.image);
     // Fallback to a different image
     e.target.src = '/images/image2.jpg';
   }}
 />
 </div>
 
 {/* Light mode overlay */}
 <div ref={overlayRef} className="absolute inset-0 z-[2] opacity-100 transition-opacity duration-500" style={{ background:`linear-gradient(to right, rgba(6,13,36,0.85) 0%, rgba(6,13,36,0.6) 45%, rgba(6,13,36,0.2) 100%), linear-gradient(to top, rgba(6,13,36,0.8) 0%, transparent 50%), linear-gradient(to bottom, rgba(6,13,36,0.4) 0%, transparent 30%)`, opacity: 0 }} />
 
 {/* Dark mode overlay */}
 <div className="absolute inset-0 z-[2] opacity-0 transition-opacity duration-500" style={{ background:`linear-gradient(to right, rgba(2,6,23,0.98) 0%, rgba(2,6,23,0.8) 45%, rgba(2,6,23,0.4) 100%), linear-gradient(to top, rgba(2,6,23,0.98) 0%, transparent 40%), linear-gradient(to bottom, rgba(2,6,23,0.6) 0%, transparent 30%)`, opacity: 0 }} />
 
 <div ref={meshRef} className="absolute inset-0 z-[3] pointer-events-none will-change-transform" style={{ background:`radial-gradient(ellipse 60% 50% at 70% 60%, rgba(245,158,11,0.15) 0%, transparent 55%), radial-gradient(ellipse 40% 40% at 30% 30%, rgba(59,130,246,0.1) 0%, transparent 50%)` }} />
 <div className="absolute inset-0 z-[4] dot-pattern opacity-10 pointer-events-none" />

 <div className="absolute inset-0 z-20 flex flex-col justify-center px-6 sm:px-12 lg:px-20 xl:px-28">
 <div className="max-w-2xl xl:max-w-3xl space-y-6 pt-24 lg:pt-16">

 <div className="space-y-2 overflow-hidden">
 <div className="overflow-hidden">
 <h1 ref={headLine1Ref} className="font-display font-black leading-none text-white" style={{ fontSize:'clamp(2.5rem, 6vw, 4.5rem)', opacity: 0 }}>
 {hero.headline}
 </h1>
 </div>
 <div className="overflow-hidden">
 <h1 ref={headLine2Ref} className="font-display font-black leading-none text-gradient-primary" style={{ fontSize:'clamp(2.5rem, 6vw, 4.5rem)', opacity: 0 }}>
 {hero.headlineAccent}
 </h1>
 </div>
 </div>

 <p ref={descRef} className="text-slate-300 leading-relaxed max-w-lg" style={{ fontSize:'clamp(0.95rem, 1.4vw, 1.1rem)', opacity: 0 }}>
 {hero.description}
 </p>
 </div>
 </div>

 <div ref={scrollCueRef} className="absolute bottom-8 left-1/2 -translate-x-1/2 z-30" style={{ opacity: 0 }}>
 <ScrollIndicator />
 </div>

 <div ref={vertLineRef} className="absolute left-0 top-1/4 bottom-1/4 w-[2px] z-20 hidden lg:block" style={{ background:'linear-gradient(to bottom, transparent, rgba(59,130,246,0.4), transparent)', transform:'scaleY(0)', transformOrigin:'top' }} />

 <div className="absolute right-6 top-1/3 hidden xl:flex flex-col items-center gap-2 pointer-events-none z-20">
 <p className="font-mono text-[10px] tracking-widest text-slate-700" style={{ writingMode:'vertical-lr' }}>COMMUNICATIONS</p>
 </div>

 <div className="absolute bottom-0 left-0 right-0 h-32 z-10 pointer-events-none" style={{ background:'linear-gradient(to top, #060d24 0%, transparent 100%)' }} />
 </section>
 );
};

export default ContactHero;
